import React, { useState, useEffect } from 'react';
import { getScheduledArticles } from '../services/api';
import { useProject } from '../contexts/ProjectContext';
import { 
  Paper, 
  Typography, 
  Box, 
  Container,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  useMediaQuery,
  Drawer,
  Button
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  subMonths,
  startOfWeek,
  endOfWeek,
  isToday
} from 'date-fns';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const ArticleCalendar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const [scheduledArticles, setScheduledArticles] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (selectedProject?.id) {
      loadScheduledArticles();
    }
  }, [selectedProject]);

  // When date is selected on mobile, open drawer
  useEffect(() => {
    if (isMobile && selectedDate) {
      setDrawerOpen(true);
    }
  }, [selectedDate, isMobile]);

  const loadScheduledArticles = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getScheduledArticles(selectedProject?.id || null);
      setScheduledArticles(data);
    } catch (error) {
      console.error("Error loading scheduled articles", error);
      setError(error.message || 'Failed to load scheduled articles');
    } finally {
      setLoading(false);
    }
  };

  const getArticlesForDate = (date) => {
    return scheduledArticles.filter(article => {
      if (!article.publish_date) return false;
      const pubDate = new Date(article.publish_date);
      return isSameDay(pubDate, date);
    });
  };

  const renderArticlePreview = (article, detailed = false) => (
    <Card 
      key={article.id} 
      variant="outlined"
      sx={{ 
        mb: 1,
        '&:hover': {
          borderColor: 'primary.main',
          cursor: 'pointer',
        }
      }}
      onClick={() => navigate(`/articles/${article.id}`)}
    >
      <CardContent sx={{ p: detailed ? 2 : 1, '&:last-child': { pb: detailed ? 2 : 1 } }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: detailed ? 1 : 0
        }}>
          <Stack spacing={detailed ? 1 : 0} sx={{ flex: 1 }}>
            <Typography 
              variant={detailed ? "subtitle1" : "body2"} 
              component="h3"
              noWrap={!detailed}
            >
              {article.title}
            </Typography>
            {detailed && (
              <Typography variant="body2" color="text.secondary">
                Scheduled for {format(new Date(article.publish_date), 'p')}
              </Typography>
            )}
          </Stack>
          <Tooltip title="Open article">
            <IconButton 
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/articles/${article.id}`);
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        
        {detailed && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
            <Chip 
              size="small"
              label={article.topic}
              color="default"
            />
            {article.tags?.map((tag) => (
              <Chip 
                key={tag}
                size="small"
                label={tag}
                variant="outlined"
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  const renderCalendarDays = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days = [];

    const formattedDate = format(currentMonth, "MMMM yyyy");

    // Calendar header with month navigation
    days.push(
      <Box 
        key="header" 
        sx={{ 
          gridColumn: '1 / -1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          px: 2
        }}
      >
        <IconButton onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h5">{formattedDate}</Typography>
        <IconButton onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
          <ChevronRightIcon />
        </IconButton>
      </Box>
    );

    // Days of week header
    const daysOfWeek = eachDayOfInterval({ start: startDate, end: endOfWeek(startDate) });
    days.push(
      ...daysOfWeek.map(day => (
        <Box 
          key={`header-${day}`}
          sx={{ 
            p: 1,
            textAlign: 'center',
            borderBottom: 1,
            borderColor: 'divider',
            backgroundColor: 'background.default'
          }}
        >
          <Typography variant="subtitle2">
            {format(day, isMobile ? 'EEEEE' : 'EEE')}
          </Typography>
        </Box>
      ))
    );

    // Calendar days
    const daysCells = eachDayOfInterval({ start: startDate, end: endDate });
    days.push(
      ...daysCells.map(day => {
        const articlesForDay = getArticlesForDate(day);
        const isCurrentMonth = isSameMonth(day, currentMonth);
        
        return (
          <Box
            key={day.toString()}
            onClick={() => setSelectedDate(day)}
            sx={{
              p: 1,
              minHeight: isMobile ? '80px' : '120px',
              borderRight: 1,
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: isToday(day) ? 'action.hover' : 
                            isSameDay(day, selectedDate) ? 'primary.light' : 
                            'background.paper',
              opacity: isCurrentMonth ? 1 : 0.4,
              cursor: 'pointer',
              overflow: 'hidden',
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 1,
                fontWeight: isToday(day) ? 'bold' : 'normal'
              }}
            >
              {format(day, 'd')}
            </Typography>
            {isMobile ? (
              articlesForDay.length > 0 && (
                <Chip 
                  size="small" 
                  label={`${articlesForDay.length} article${articlesForDay.length > 1 ? 's' : ''}`}
                  color="primary"
                />
              )
            ) : (
              <>
                {articlesForDay.slice(0, 3).map(article => renderArticlePreview(article))}
                {articlesForDay.length > 3 && (
                  <Typography variant="caption" color="text.secondary">
                    +{articlesForDay.length - 3} more
                  </Typography>
                )}
              </>
            )}
          </Box>
        );
      })
    );

    return days;
  };

  const selectedDateArticles = getArticlesForDate(selectedDate);

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Typography variant="h4" component="h1">
          Publication Calendar
        </Typography>
        
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper 
            elevation={0}
            sx={{ 
              p: 2,
              backgroundColor: 'background.paper',
            }}
          >
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0,
              border: 1,
              borderColor: 'divider',
            }}>
              {renderCalendarDays()}
            </Box>
          </Paper>
        )}

        {/* Desktop Selected Day Details */}
        {!isMobile && selectedDate && (
          <Paper elevation={0} sx={{ p: 2, backgroundColor: 'background.paper' }}>
            <Typography variant="h6" gutterBottom>
              Articles for {format(selectedDate, 'PPPP')}
            </Typography>
            <Stack spacing={2}>
              {selectedDateArticles.length === 0 ? (
                <Typography color="text.secondary">
                  No articles scheduled for this date.
                </Typography>
              ) : (
                selectedDateArticles.map(article => renderArticlePreview(article, true))
              )}
            </Stack>
          </Paper>
        )}
      </Stack>

      {/* Mobile Drawer for Selected Day */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          PaperProps={{
            sx: {
              maxHeight: '80vh',
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              px: 2,
              pb: 2,
            }
          }}
        >
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            py: 2,
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paper',
            zIndex: 1,
          }}>
            <Typography variant="h6">
              {format(selectedDate, 'PPPP')}
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>
          <Stack spacing={2}>
            {selectedDateArticles.length === 0 ? (
              <Typography color="text.secondary">
                No articles scheduled for this date.
              </Typography>
            ) : (
              selectedDateArticles.map(article => renderArticlePreview(article, true))
            )}
          </Stack>
        </Drawer>
      )}
    </Container>
  );
};

export default ArticleCalendar; 